export const SMASHPARTY_SESSION_STORAGE_KEY = 'smashparty-session';

export type StoredSmashpartySessionMode = 'game' | 'warmup-arena';

export interface StoredSmashpartySession {
  token: string;
  roomCode: string;
  savedAt: string;
  mode?: StoredSmashpartySessionMode;
  phase?: string;
}

function getSessionStore(): Storage | null {
  return typeof sessionStorage === 'undefined' ? null : sessionStorage;
}

function getLegacyLocalStore(): Storage | null {
  return typeof localStorage === 'undefined' ? null : localStorage;
}

function clearLegacyStoredSession(): void {
  try {
    getLegacyLocalStore()?.removeItem(SMASHPARTY_SESSION_STORAGE_KEY);
  } catch {
    // Ignore storage failures in private or restricted modes.
  }
}

function isStoredSession(value: unknown): value is StoredSmashpartySession {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.token === 'string' &&
    candidate.token.length > 0 &&
    typeof candidate.roomCode === 'string' &&
    candidate.roomCode.length > 0 &&
    typeof candidate.savedAt === 'string' &&
    candidate.savedAt.length > 0 &&
    (candidate.mode === undefined ||
      candidate.mode === 'game' ||
      candidate.mode === 'warmup-arena') &&
    (candidate.phase === undefined || typeof candidate.phase === 'string')
  );
}

export function readStoredSession(): StoredSmashpartySession | null {
  clearLegacyStoredSession();
  const storage = getSessionStore();
  if (storage == null) return null;

  let raw: string | null = null;
  try {
    raw = storage.getItem(SMASHPARTY_SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw == null) return null;

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (isStoredSession(parsed)) return parsed;
  } catch {
    // Invalid JSON falls through to clearing the stale entry.
  }

  try {
    storage.removeItem(SMASHPARTY_SESSION_STORAGE_KEY);
  } catch {
    // Ignore storage failures in private or restricted modes.
  }
  return null;
}

export function writeStoredSession(session: Omit<StoredSmashpartySession, 'savedAt'>): void {
  clearLegacyStoredSession();
  const storage = getSessionStore();
  if (storage == null) return;

  try {
    storage.setItem(
      SMASHPARTY_SESSION_STORAGE_KEY,
      JSON.stringify({
        token: session.token,
        roomCode: session.roomCode,
        ...(session.mode === undefined ? {} : { mode: session.mode }),
        ...(session.phase === undefined ? {} : { phase: session.phase }),
        savedAt: new Date().toISOString(),
      } satisfies StoredSmashpartySession),
    );
  } catch {
    // Ignore storage failures in private or restricted modes.
  }
}

export function clearStoredSession(): void {
  try {
    getSessionStore()?.removeItem(SMASHPARTY_SESSION_STORAGE_KEY);
  } catch {
    // Ignore storage failures in private or restricted modes.
  }
  clearLegacyStoredSession();
}
